import React, { useState } from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import {
  Button,
  Icon,
  Menu,
  Modal,
  Popup,
  TransitionablePortal,
} from "semantic-ui-react";

import { cardFormat, draggableTracks } from "./atoms";
import { snoozeModal } from "./atoms";
import { playlistFilterActive } from "./atoms";

import { Transport } from "./Transport";
import { ShowLyricsToggle } from "./ShowLyricsToggle";
import { DescriptionButton } from "./DescriptionButton";

import { options$ } from "./OptionsStream";
import { commands$ } from "./CommandsStream";

import { historyState } from "./HistoryMachine";
import { VersionCard } from "./VersionCard";
import { useIsMobile } from "./utils";
import { LoopMenuItem } from "./LoopMenuItem";
import { TimelineButton } from "./TimelineButton";

function CardFormatOption() {
  const [format, setFormat] = useRecoilState(cardFormat);
  const active = format === "small";

  const changeFormat = () => {
    const newFormat = active ? "normal" : "small";

    options$.cardFormat(newFormat);

    setFormat(newFormat);
  };

  return (
    <Menu.Item onClick={changeFormat} active={active}>
      <Icon name="window minimize" />
      <br />
      Small Cards
    </Menu.Item>
  );
}

function SnoozeOption() {
  const [isOpen, setOpen] = useRecoilState(snoozeModal);

  const toggleModal = () => {
    setOpen(!isOpen);
  };

  return (
    <Menu.Item onClick={toggleModal} active={isOpen}>
      <Icon name="hourglass start" />
      <br />
      Snooze
    </Menu.Item>
  );
}

function MobileSortItem() {
  return (
    <Menu.Item name="sort" onClick={() => commands$.reorder()}>
      <Icon name="sort" />
      <br />
      Sort
    </Menu.Item>
  );
}

function DesktopSortItem() {
  const [isOpen, setOpen] = useState(false);
  const [draggableCards, setDraggableCards] = useRecoilState(draggableTracks);

  const enableDragging = () => {
    setDraggableCards(!draggableCards);
    setOpen(false);
  };
  const reorder = () => {
    commands$.reorder();
    setOpen(false);
  };

  return draggableCards ? (
    <Menu.Item name="sort" onClick={() => setDraggableCards(false)}>
      <Icon name="sort" color={draggableCards ? "blue" : undefined} />
    </Menu.Item>
  ) : (
    <Popup
      wide
      on="click"
      open={isOpen}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      content={
        <Button.Group>
          <Button color="teal" onClick={reorder}>
            Assign Indexes
          </Button>
          <Button.Or />
          <Button color="blue" onClick={enableDragging}>
            Drag Tracks
          </Button>
        </Button.Group>
      }
      trigger={
        <Menu.Item name="sort">
          <Icon name="sort" />
        </Menu.Item>
      }
    />
  );
}

function Commands() {
  const history = useRecoilValue(historyState);
  const filterActive = useRecoilValue(playlistFilterActive);

  return (
    <>
      <DesktopSortItem />

      <Menu.Item name="shuffle" onClick={() => commands$.shuffle()}>
        <Icon name="shuffle" />
      </Menu.Item>

      <Menu.Item />

      <Menu.Item
        name="undo"
        disabled={!history.hasPast}
        onClick={() => commands$.undo()}
      >
        <Icon name="undo" />
      </Menu.Item>
      <Menu.Item
        name="redo"
        disabled={!history.hasFuture}
        onClick={() => commands$.redo()}
      >
        <Icon name="redo" />
      </Menu.Item>

      <Menu.Item />

      <Menu.Item name="filter" onClick={() => commands$.filter()}>
        <Icon name="filter" color={filterActive ? "red" : undefined} />
      </Menu.Item>

      <ShowLyricsToggle />

      {global.features.saveEnabled ? (
        <>
          <Menu.Item />

          <Menu.Item name="save" onClick={() => commands$.save()}>
            <Icon name="save" />
          </Menu.Item>
        </>
      ) : null}
    </>
  );
}

const isFullscreen = () => document.fullscreenElement !== null;

export function PlayerSubMenu({ open, onClose }) {
  const isMobileDevice = useIsMobile();
  const history = useRecoilValue(historyState);
  const filterActive = useRecoilValue(playlistFilterActive);

  const toggleScreenMode = () => {
    if (isFullscreen()) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }

  return (
    <TransitionablePortal
      open={open}
      transition={{ animation: "fade", duration: 500 }}
    >
      <Modal
        dimmer="inverted"
        open={true}
        onClose={onClose}
        onClick={onClose}
        centered={false}
        style={{ position: "fixed", top: 40 }}
      >
        <Modal.Content className="player-menu">
          {/* row 1 */}
          <Menu.Item
            name="undo"
            disabled={!history.hasPast}
            onClick={() => commands$.undo()}
          >
            <Icon name="undo" />
            <br />
            Undo
          </Menu.Item>
          <Menu.Item
            name="redo"
            disabled={!history.hasFuture}
            onClick={() => commands$.redo()}
          >
            <Icon name="redo" />
            <br />
            Redo
          </Menu.Item>

          {global.features.saveEnabled ? (
            <Menu.Item name="save" onClick={() => commands$.save()}>
              <Icon name="save" />
              <br />
              Save
            </Menu.Item>
          ) : (
            <Menu.Item />
          )}

          {/* row 2 */}
          <Menu.Item name="filter" onClick={() => commands$.filter()}>
            <Icon name="filter" color={filterActive ? "red" : undefined} />
            <br />
            Filter
          </Menu.Item>

          <MobileSortItem />

          <Menu.Item name="shuffle" onClick={() => commands$.shuffle()}>
            <Icon name="shuffle" />
            <br />
            Shuffle
          </Menu.Item>

          {/* row 3 */}
          <ShowLyricsToggle showLabel={true} />
          <CardFormatOption />

          <Menu.Item name="expand" onClick={() => toggleScreenMode()} active={isFullscreen()}>
            <Icon name={isFullscreen() ? "compress" : "expand"} />
            <br />
            Full screen
          </Menu.Item>

          {/* row 4 */}
          <TimelineButton />

          {isMobileDevice
            ? <LoopMenuItem />
            : global.features.includeDescription
              ? <DescriptionButton showLabel={true} />
              : null
          }

          <SnoozeOption />

          <VersionCard />
        </Modal.Content>
      </Modal>
    </TransitionablePortal >
  );
}

function DesktopToolbar(props) {
  const [menuShown, setMenuShown] = useState(false);
  const [volumesShown, setVolumesShown] = useState(false);

  return (
    <Menu icon>
      <Menu.Item
        name="bars"
        onClick={() => {
          setMenuShown(true);
        }}
      >
        <Icon name="bars" />
      </Menu.Item>

      <PlayerSubMenu open={menuShown} onClose={() => setMenuShown(false)} />

      {global.features.includeTour ? (
        <Menu.Item
          name="help"
          onClick={() => {
            commands$.startTour();
          }}
        >
          <Icon name="help" />
        </Menu.Item>
      ) : null}

      {global.features.includeDescription ? (
        <DescriptionButton {...props} />
      ) : null}

      <Menu.Item />

      <Commands {...props} />

      <Menu.Item />

      <Menu.Item
        name="volume"
        onClick={() => {
          setVolumesShown(!volumesShown);

          commands$.toggleVolumes();
        }}
      >
        <Icon name="volume up" color={volumesShown ? "blue" : undefined} />
      </Menu.Item>

      <Transport {...props} />

      <Menu.Item position="right">{props.children}</Menu.Item>
    </Menu>
  );
}

export { DesktopToolbar as Toolbar };
